import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDateRange } from "../../analytics/hooks/useDateRange";
import type { ListFeedbackItemsParams } from "../../generated/core-api";
import { FeedbackValue } from "../../generated/core-api";

const DEFAULT_FEEDBACK_VALUES = [FeedbackValue.thumbs_up] as const;

export interface FeedbackReviewFiltersState {
  dateRange: ReturnType<typeof useDateRange>;
  jurisdiction: string;
  searchText: string;
  workspaceId: string;
  orgId: string;
  filterParams: ListFeedbackItemsParams;
  setJurisdiction: (value: string) => void;
  setSearchText: (value: string) => void;
  setWorkspaceId: (value: string) => void;
  setOrgId: (value: string) => void;
  clearOptionalFilters: () => void;
  hasOptionalFilters: boolean;
}

function setParam(
  prev: URLSearchParams,
  key: string,
  value: string,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (value) {
    next.set(key, value);
  } else {
    next.delete(key);
  }
  return next;
}

export function useFeedbackReviewFilters(): FeedbackReviewFiltersState {
  const dateRange = useDateRange();
  const [searchParams, setSearchParams] = useSearchParams();

  const jurisdiction = searchParams.get("jurisdiction") ?? "";
  const searchText = searchParams.get("search") ?? "";
  const workspaceId = searchParams.get("workspace") ?? "";
  const orgId = searchParams.get("org") ?? "";

  const filterParams = useMemo<ListFeedbackItemsParams>(
    () => ({
      period_start: dateRange.fromDate,
      period_end: dateRange.toDate,
      feedback_values: [...DEFAULT_FEEDBACK_VALUES],
      search_text: searchText || undefined,
      jurisdictions: jurisdiction ? [jurisdiction] : undefined,
      workspace_ids: workspaceId ? [workspaceId] : undefined,
      org_ids: orgId ? [orgId] : undefined,
    }),
    [
      dateRange.fromDate,
      dateRange.toDate,
      jurisdiction,
      searchText,
      workspaceId,
      orgId,
    ],
  );

  const setJurisdiction = useCallback(
    (value: string) => {
      setSearchParams((prev) => setParam(prev, "jurisdiction", value), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  const setSearchText = useCallback(
    (value: string) => {
      setSearchParams((prev) => setParam(prev, "search", value), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  const setWorkspaceId = useCallback(
    (value: string) => {
      setSearchParams((prev) => setParam(prev, "workspace", value), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  const setOrgId = useCallback(
    (value: string) => {
      setSearchParams((prev) => setParam(prev, "org", value), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  const clearOptionalFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("jurisdiction");
        next.delete("search");
        next.delete("workspace");
        next.delete("org");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const hasOptionalFilters = Boolean(
    jurisdiction || searchText || workspaceId || orgId,
  );

  return {
    dateRange,
    jurisdiction,
    searchText,
    workspaceId,
    orgId,
    filterParams,
    setJurisdiction,
    setSearchText,
    setWorkspaceId,
    setOrgId,
    clearOptionalFilters,
    hasOptionalFilters,
  };
}
